import React, { useEffect, useState } from "react"
import { TouchableOpacity, StyleSheet } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { ColumnFlex, CenterFlex, BetweenFlex, StartFlex, Text, Button, IconFont, Hr } from "~components/El"
import RNOTPInput from "~components/VerifyCodeInput/components/RNOTPInput"
import { usePopup } from "~hooks/usePopup"
import { globalToast } from "~utils"
import { useStores } from "~models"

export type SecurityVerificationPopupProps = {
  isEmail: boolean
  account: string
  /** 脱敏后的手机/邮箱展示 */
  maskedContact: string
  resendCooldown?: number
  onVerify: (code: string) => Promise<void>
}

const SecurityVerificationPopup: React.FC<SecurityVerificationPopupProps> = ({
  isEmail,
  account,
  maskedContact,
  resendCooldown = 60,
  onVerify,
}) => {
  const theme = useTheme()
  const popup = usePopup()
  const { apiStore } = useStores()
  const [code, setCode] = useState("")
  const [timeLeft, setTimeLeft] = useState(resendCooldown)
  const [loading, setLoading] = useState(false)
  const [isInvalid, setIsInvalid] = useState(false)
  

  useEffect(() => {
    if (timeLeft <= 0) return undefined
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [timeLeft])

  const handleClose = () => {
    popup.close()
  }

  const handleResend = async () => {
    if (timeLeft > 0) return
    setTimeLeft(resendCooldown)
    try {
        if (isEmail) {
          apiStore.getEmailSms({ email: account, event: "bind_email" }).then((res) => { 
            if (res?.code === 200) {
              globalToast.show(res?.message || "发送成功")
            }
          }).catch((error) => {
            globalToast.show(error?.message || "发送失败")
          })
        }
        if (!isEmail) {
          apiStore.sendPhoneCodeByNotBound({ phone: account, event: "register_phone", country_code: "+86" }).then((res) => {
            if (res?.code === 200) {
              globalToast.show(res?.message || "发送成功")
            }
          }).catch((error) => { 
            globalToast.show(error?.message || "发送失败")
          })
        }
    } catch (e: any) {
      globalToast.show(e?.message || "发送失败")
    }
  }

  const handleConfirm = async () => {
    if (code.length !== 6) {
      setIsInvalid(true)
      setTimeout(() => setIsInvalid(false), 800)
      return
    }
    setLoading(true)
    try {
      await onVerify(code)
      popup.close()
    } catch (e: any) {
      globalToast.show(e?.message || "验证失败")
    } finally {
      setLoading(false)
    }
  }

  const busy = loading

  return (
    <Shell>
      <BetweenFlex height="auto" width="100%" alignItems="center" padding="16px 16px 12px">
        <StartFlex height="auto">
          <Text fontSize={theme.fontSizes.xl} fontWeight="700" color={theme.colors.text} ml="10px">
            安全验证
          </Text>
        </StartFlex>
        <TouchableOpacity onClick={handleClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <IconFont name="close" size={16} color={theme.colors.secondary} />
        </TouchableOpacity>
      </BetweenFlex>
      <Hr />

      <ColumnFlex width="100%" padding="20px 16px 16px" alignItems="center">
        <Text fontSize={theme.fontSizes.lg} fontWeight="600" color={theme.colors.text}>
          检测到新设备登录
        </Text>
        <CenterFlex height={'auto'} flexDirection="row" mt="10px" flexWrap="wrap">
          <Text fontSize={theme.fontSizes.md} color={theme.colors.secondaryText}>
            验证码已发送至{" "}
          </Text>
          <Text fontSize={theme.fontSizes.md} color={theme.colors.text}>
            {maskedContact || "—"}
          </Text>
        </CenterFlex>
        <Text mt="16px" fontSize={theme.fontSizes.sm} color={theme.colors.secondaryText}>
          请输入6位验证码
        </Text>

        <ColumnFlex flexShrink={0} width="100%" height={48} mt="14px" padding="0 4px" alignItems="center">
          <RNOTPInput
            length={6}
            value={code}
            onChangeText={setCode}
            isInvalid={isInvalid}
            cellStyle={{ width: 44, height: 48, borderRadius: 8 }}
            autoFocus
          />
        </ColumnFlex>

        <Text height="auto" mt="14px" fontSize={theme.fontSizes.sm} color={theme.colors.secondaryText}>
          {timeLeft > 0 ? `${timeLeft}秒后重新发送` : "可重新发送验证码"}
        </Text>

        <TouchableOpacity onClick={handleResend} disabled={timeLeft > 0} style={{ marginTop: 14 }}>
          <Text fontSize={theme.fontSizes.md} color={timeLeft > 0 ? theme.colors.secondaryText : theme.colors.warning}>
            没有收到验证码？
          </Text>
        </TouchableOpacity>
      </ColumnFlex>

      <BetweenFlex height="auto" width="100%" padding="12px 16px 18px" gap="12px">
        <OutlineButton flex={1} height="44px" borderRadius="10px" onClick={handleClose} disabled={busy}>
          <Text fontSize={theme.fontSizes.lg} color={theme.colors.text}>
            取消
          </Text>
        </OutlineButton>
        <SolidButton
          flex={1}
          height="44px"
          borderRadius="10px"
          loading={busy}
          disabled={busy || code.length !== 6}
          loadingText="验证中"
          onClick={handleConfirm}
        >
          <Text fontSize={theme.fontSizes.lg} fontWeight="600" color="#fff">
            确认验证
          </Text>
        </SolidButton>
      </BetweenFlex>
    </Shell>
  )
}

const Shell = styled(ColumnFlex)`
  background-color: #fff;
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
`

const OutlineButton = styled(Button)`
  background-color: #fff;
  border-width: ${StyleSheet.hairlineWidth}px;
  border-color: ${({ theme }) => theme.colors.border};
  border-style: solid;
`

const SolidButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary};
`

export default SecurityVerificationPopup
